import { parseTokens } from "@/shared/utils/resolve-tokens";
import type { RepeatableField } from "./types";

/** The parts of an org variable definition this module reads. */
export interface FieldDefinitionLike {
  name?: string;
  title?: string;
  type?: string;
  prompt?: string;
}

// Filled by Storydoc from the sender / org, never a row field.
const isAutoFilled = (name: string): boolean =>
  name.startsWith("sender_") || name.startsWith("organization_");

/**
 * Variable names an AI prompt reads, as ROW field keys of `groupKey`:
 * `{{group.field}}` loses its group prefix, other namespaced tokens
 * (`hubspot.contact.email`) and auto-filled names are not row fields.
 */
export const promptDependencyNames = (
  prompt: string | undefined,
  groupKey: string,
): string[] => {
  if (!prompt) return [];
  const names = new Set<string>();
  for (const segment of parseTokens(prompt)) {
    if (segment.type !== "var") continue;
    const name = segment.name.startsWith(`${groupKey}.`)
      ? segment.name.slice(groupKey.length + 1)
      : segment.name;
    if (!name || name.includes(".") || isAutoFilled(name)) continue;
    names.add(name);
  }
  return Array.from(names);
};

/**
 * Row fields an AI field needs but the group does not declare yet. An AI
 * prompt like "Pitch {{industry}}" inside a repeatable means a per-row
 * `industry` — each row has its own — so the dependency becomes a field of
 * the group (typed from the org variable when known, else a text input).
 * Fields already declared here or on a parent row are not repeated.
 */
export const promptDependencyFields = (
  fields: RepeatableField[],
  definitions: ReadonlyMap<string, FieldDefinitionLike>,
  groupKey: string,
  parentFieldKeys: ReadonlySet<string> = new Set(),
): RepeatableField[] => {
  const present = new Set(fields.map((f) => f.key));
  const out: RepeatableField[] = [];
  for (const field of fields) {
    if (field.type !== "ai") continue;
    const prompt = definitions.get(field.key)?.prompt;
    for (const name of promptDependencyNames(prompt, groupKey)) {
      if (present.has(name) || parentFieldKeys.has(name)) continue;
      present.add(name);
      const def = definitions.get(name);
      out.push({
        key: name,
        name: def?.title || name,
        type: def?.type && def.type !== "ai" ? def.type : "input",
      });
    }
  }
  return out;
};

/** `fields` with each AI field's missing dependencies inserted right before it. */
export const withPromptDependencyFields = (
  fields: RepeatableField[],
  definitions: ReadonlyMap<string, FieldDefinitionLike>,
  groupKey: string,
  parentFieldKeys: ReadonlySet<string> = new Set(),
): RepeatableField[] => {
  const extra = promptDependencyFields(
    fields,
    definitions,
    groupKey,
    parentFieldKeys,
  );
  if (!extra.length) return fields;
  const byAiField = new Map<string, RepeatableField[]>();
  const present = new Set(fields.map((f) => f.key));
  for (const field of fields) {
    if (field.type !== "ai") continue;
    const own = promptDependencyNames(
      definitions.get(field.key)?.prompt,
      groupKey,
    )
      .filter((name) => !present.has(name) && !parentFieldKeys.has(name))
      .map((name) => extra.find((e) => e.key === name))
      .filter((e): e is RepeatableField => !!e);
    for (const e of own) present.add(e.key);
    byAiField.set(field.key, own);
  }
  const out: RepeatableField[] = [];
  for (const field of fields) {
    for (const dep of byAiField.get(field.key) ?? []) out.push(dep);
    out.push(field);
  }
  return out;
};

/**
 * Org variable definitions from the RTK Query cache of the fast-vars endpoint,
 * whatever argument the callers used to key it. For non-React code (thunks).
 */
export const selectFastVarDefinitions = (
  state: unknown,
): Map<string, FieldDefinitionLike> => {
  const map = new Map<string, FieldDefinitionLike>();
  const queries = (state as { api?: { queries?: Record<string, unknown> } })
    ?.api?.queries;
  if (!queries) return map;
  for (const entry of Object.values(queries)) {
    const q = entry as
      | { endpointName?: string; status?: string; data?: unknown }
      | undefined;
    // Not gated on `status`: during a refetch RTK keeps the last data while
    // the status is "pending", and the definitions must not vanish then.
    if (q?.endpointName !== "getFastVars") continue;
    if (!Array.isArray(q.data)) continue;
    for (const row of q.data as Array<{
      archived?: boolean;
      variable?: FieldDefinitionLike;
    }>) {
      const def = row?.variable;
      if (!row?.archived && def?.name && !map.has(def.name))
        map.set(def.name, def);
    }
  }
  return map;
};
